import React from 'react';
import { X } from 'lucide-react';

// Type options — Feed (1:1) and Story (9:16)
export const TYPE_OPTIONS = [
  { value: '1:1',  label: 'Feed'  },
  { value: '9:16', label: 'Story' },
];

// Status options shown to the user (no draft/failed/regenerating)
export const STATUS_OPTIONS = [
  { value: 'pending',   label: 'Pending'   },
  { value: 'approved',  label: 'Approved'  },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
  { value: 'stopped',   label: 'Stopped'   },
];

/**
 * GalleryFilterBar
 *
 * Props:
 *   selectedTypes:   string[]   — active aspectRatio filters
 *   selectedStatuses: string[]  — active status filters
 *   onToggleType:    (value) => void
 *   onToggleStatus:  (value) => void
 *   onClearAll:      () => void
 *   totalCount:      number     — total posts before filtering
 *   filteredCount:   number     — posts after filtering
 */
const GalleryFilterBar = ({
  selectedTypes,
  selectedStatuses,
  onToggleType,
  onToggleStatus,
  onClearAll,
  totalCount,
  filteredCount,
}) => {
  const hasFilters = selectedTypes.length > 0 || selectedStatuses.length > 0;

  return (
    <div className='space-y-3'>
      {/* Type filters */}
      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-xs font-semibold uppercase tracking-wide shrink-0' style={{ color: 'var(--text-muted)' }}>
          Type
        </span>
        {TYPE_OPTIONS.map((opt) => {
          const active = selectedTypes.includes(opt.value);
          return (
            <button
              key={opt.value}
              type='button'
              onClick={() => onToggleType(opt.value)}
              className='px-3 py-1 rounded-full text-xs font-medium transition-colors border'
              style={{
                background:   active ? 'var(--brand)'       : 'var(--bg-surface)',
                color:        active ? 'var(--brand-text)'  : 'var(--text-body)',
                borderColor:  active ? 'var(--brand)'       : 'var(--border)',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Status filters */}
      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-xs font-semibold uppercase tracking-wide shrink-0' style={{ color: 'var(--text-muted)' }}>
          Status
        </span>
        {STATUS_OPTIONS.map((opt) => {
          const active = selectedStatuses.includes(opt.value);
          return (
            <button
              key={opt.value}
              type='button'
              onClick={() => onToggleStatus(opt.value)}
              className='px-3 py-1 rounded-full text-xs font-medium transition-colors border'
              style={{
                background:   active ? 'var(--brand)'       : 'var(--bg-surface)',
                color:        active ? 'var(--brand-text)'  : 'var(--text-body)',
                borderColor:  active ? 'var(--brand)'       : 'var(--border)',
              }}
            >
              {opt.label}
            </button>
          );
        })}

        {/* Clear all */}
        {hasFilters && (
          <button
            type='button'
            onClick={onClearAll}
            className='flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors border'
            style={{
              background:  'transparent',
              color:       'var(--text-muted)',
              borderColor: 'var(--border)',
            }}
          >
            <X className='w-3 h-3' />
            Clear
          </button>
        )}
      </div>

      {/* Result count — only shown when filters are active */}
      {hasFilters && (
        <p className='text-xs' style={{ color: 'var(--text-muted)' }}>
          Showing {filteredCount} of {totalCount} posts
        </p>
      )}
    </div>
  );
};

export default GalleryFilterBar;
